import { resolvePreset, type DatePreset, type DateRange } from '@/shared/ui/DateRangePicker';

export interface PerformanceFilters {
  range: DateRange;
  preset: DatePreset;
  platforms: string[];
  channels: string[];
  campaignIds: string[];
}

const DEFAULT_PRESET = 'last_30_days' as const;

const RESOLVABLE_PRESETS = [
  'last_7_days',
  'last_14_days',
  'last_30_days',
  'this_month',
  'last_month',
] as const;

type ResolvablePreset = (typeof RESOLVABLE_PRESETS)[number];

function isResolvable(value: string): value is ResolvablePreset {
  return (RESOLVABLE_PRESETS as readonly string[]).includes(value);
}

function list(params: URLSearchParams, key: string): string[] {
  const raw = params.get(key);
  if (!raw) return [];
  return raw.split(',').filter(Boolean);
}

/**
 * Reads filters out of the URL.
 *
 * A named preset is always RE-RESOLVED against today rather than trusting the dates in
 * the URL. A bookmarked "last 7 days" has to mean the last 7 days when it is opened —
 * honouring the stale dates would silently show a month-old week under a label claiming
 * otherwise.
 *
 * Anything unparseable falls back to the default rather than throwing: these values are
 * user-editable text, and a hand-mangled URL should show a dashboard, not an error.
 */
export function parseFilterParams(params: URLSearchParams, today: string): PerformanceFilters {
  const requested = params.get('preset') ?? DEFAULT_PRESET;
  const from = params.get('from');
  const to = params.get('to');

  const shared = {
    /* eslint-disable i18next/no-literal-string -- URL search-param names, not copy */
    platforms: list(params, 'platforms'),
    channels: list(params, 'channels'),
    campaignIds: list(params, 'campaigns'),
    /* eslint-enable i18next/no-literal-string */
  };

  if (requested === 'custom' && from && to) {
    return { ...shared, preset: 'custom', range: { startDate: from, endDate: to } };
  }

  // 'flight_to_date' resolves from the project's campaigns, which this pure function does
  // not have; the dashboard overrides the range once it knows them.
  if (requested === 'flight_to_date' && from && to) {
    return { ...shared, preset: 'flight_to_date', range: { startDate: from, endDate: to } };
  }

  const preset = isResolvable(requested) ? requested : DEFAULT_PRESET;
  return { ...shared, preset, range: resolvePreset(preset, today) };
}

/** The inverse. Empty values are omitted so an untouched dashboard keeps a clean URL. */
export function toFilterParams(filters: PerformanceFilters): Record<string, string> {
  const params: Record<string, string> = { preset: filters.preset };

  // Dates are only worth writing when they cannot be recomputed from the preset.
  if (filters.preset === 'custom' || filters.preset === 'flight_to_date') {
    params.from = filters.range.startDate;
    params.to = filters.range.endDate;
  }
  if (filters.platforms.length) params.platforms = filters.platforms.join(',');
  if (filters.channels.length) params.channels = filters.channels.join(',');
  if (filters.campaignIds.length) params.campaigns = filters.campaignIds.join(',');

  return params;
}
